import React, { useState } from 'react';
import { Receipt, Scale, Tags } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useAuth } from '../../hooks/useAuth';
import { FixedCostsPanel } from './FixedCostsPanel';
import { PricesPanel } from './PricesPanel';
import { ReportPanel } from './ReportPanel';

type Tab = 'report' | 'prices' | 'fixed';

/**
 * Money in and out: what PayIQ earns in every app against what it pays its
 * providers, the people it rewards, and its fixed costs.
 *
 * The other two tabs are the inputs only a person can supply: what each
 * provider charges per use, and the bills no call records.
 */
export const Finance: React.FC = () => {
  usePage('Money in and out');
  const { can } = useAuth();
  const canManage = can('finance.manage');
  const [tab, setTab] = useState<Tab>('report');

  const tabs: { key: Tab; label: string; icon: typeof Scale }[] = [
    { key: 'report', label: 'Report', icon: Scale },
    { key: 'prices', label: 'Provider prices', icon: Tags },
    { key: 'fixed', label: 'Fixed costs', icon: Receipt },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div role="tablist" style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--neutral-300)' }}>
        {tabs.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px',
                fontSize: 14,
                fontWeight: active ? 600 : 500,
                fontFamily: 'var(--font-family)',
                color: active ? 'var(--blue-600)' : 'var(--neutral-700)',
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${active ? 'var(--blue-600)' : 'transparent'}`,
                cursor: 'pointer',
              }}
            >
              <Icon size={16} aria-hidden />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'report' && <ReportPanel onSetPrices={() => setTab('prices')} />}
      {tab === 'prices' && <PricesPanel canManage={canManage} />}
      {tab === 'fixed' && <FixedCostsPanel canManage={canManage} />}
    </div>
  );
};
